import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Avatar, DataTable, Dialog, ErrorNote, FlashNote, Loading, PageHead, Pill, RowCard, useDialogClose, useFlash } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime } from '../lib/format';
import { useStaff } from '../lib/queries';
import { qk } from '../lib/realtime-cache';
import type { Role, Staff } from '../lib/types';

function AddStaff({ onClose, onDone }: { onClose: () => void; onDone: (t: string) => void }) {
  return (
    <Dialog title="Add staff" onClose={onClose}>
      <AddStaffForm onDone={onDone} />
    </Dialog>
  );
}

function AddStaffForm({ onDone }: { onDone: (t: string) => void }) {
  const onClose = useDialogClose();
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'ADMIN' as Role });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
      <form
        className="stack-sm"
        style={{ gap: 16, marginTop: 4 }}
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api.post<Staff>('/admin/staff', { ...form, email: form.email.trim(), name: form.name.trim() });
            await qc.invalidateQueries({ queryKey: qk.staff });
            onDone(`${form.name.trim()} can now sign in.`);
            onClose();
          } catch (err) {
            setError(errorMessage(err));
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span className="field-label">Name</span>
          <input className="input" required maxLength={80} value={form.name} onChange={set('name')} />
        </label>
        <label className="field">
          <span className="field-label">Email</span>
          <input className="input" type="email" required value={form.email} onChange={set('email')} />
        </label>
        <label className="field">
          <span className="field-label">Starting password</span>
          <input className="input" type="text" required minLength={10} autoComplete="new-password" value={form.password} onChange={set('password')} />
          <span className="field-hint">At least 10 characters with a letter and a number. Share it privately.</span>
        </label>
        <label className="field">
          <span className="field-label">Role</span>
          <select className="select" value={form.role} onChange={set('role')}>
            <option value="ADMIN">Admin — runs the day, can’t change fares, settings or staff</option>
            <option value="OWNER">Owner — full control</option>
          </select>
        </label>
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-dark" disabled={busy}>
            {busy ? 'Adding…' : 'Add staff'}
          </button>
        </div>
      </form>
  );
}

export function StaffPage() {
  const { admin, isOwner } = useAuth();
  const q = useStaff();
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  /** The change shows at once (optimistic); the note offers Undo, which sends the opposite change. */
  const update = async (s: Staff, patch: Partial<Pick<Staff, 'role' | 'isActive'>>, done: string, undoable = true) => {
    const before = qc.getQueryData<Staff[]>(qk.staff);
    qc.setQueryData<Staff[]>(qk.staff, (list) => list?.map((x) => (x.id === s.id ? { ...x, ...patch } : x)));
    setBusyId(s.id);
    try {
      await api.patch<Staff>(`/admin/staff/${s.id}`, patch);
      const back: Partial<Pick<Staff, 'role' | 'isActive'>> = patch.role ? { role: s.role } : { isActive: s.isActive };
      show(done, 'good', undoable ? { label: 'Undo', run: () => update({ ...s, ...patch }, back, `Back as it was for ${s.name}.`, false) } : undefined);
    } catch (err) {
      qc.setQueryData(qk.staff, before);
      show(errorMessage(err), 'bad');
    } finally {
      setBusyId(null);
      void qc.invalidateQueries({ queryKey: qk.staff });
    }
  };
  const role = (s: Staff) => (s.role === 'OWNER' ? <Pill tone="accent">Owner</Pill> : <Pill>Admin</Pill>);
  const state = (s: Staff) => (s.isActive ? <Pill tone="good">Active</Pill> : <Pill tone="bad">Turned off</Pill>);
  const controls = (s: Staff) =>
    isOwner && s.id !== admin?.id ? (
      <span className="row" style={{ gap: 8, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
        <button
          className="btn btn-sm"
          disabled={busyId === s.id}
          onClick={() => update(s, { role: s.role === 'OWNER' ? 'ADMIN' : 'OWNER' }, s.role === 'OWNER' ? `${s.name} is now an admin.` : `${s.name} is now an owner.`)}
        >
          {s.role === 'OWNER' ? 'Make admin' : 'Make owner'}
        </button>
        <button
          className={`btn btn-sm ${s.isActive ? 'btn-danger' : ''}`}
          disabled={busyId === s.id}
          onClick={() => update(s, { isActive: !s.isActive }, s.isActive ? `${s.name} can no longer sign in.` : `${s.name} can sign in again.`)}
        >
          {s.isActive ? 'Turn off' : 'Turn on'}
        </button>
      </span>
    ) : null;

  return (
    <div className="stack">
      <PageHead
        title="Staff"
        sub="Who can sign in to this site. Owners have full control; admins run the day."
        actions={
          isOwner ? (
            <button className="btn btn-dark" onClick={() => setAdding(true)}>
              Add staff
            </button>
          ) : (
            <Pill>View only</Pill>
          )
        }
      />
      <FlashNote flash={flash} />
      <section className="card">
        {q.isLoading ? (
          <Loading />
        ) : q.error ? (
          <div className="card-body">
            <ErrorNote error={q.error} />
          </div>
        ) : (
          <DataTable
            label="Staff"
            rows={q.data ?? []}
            rowKey={(s) => s.id}
            columns={[
              {
                key: 'name',
                header: 'Name',
                cell: (s) => (
                  <div className="person">
                    <Avatar name={s.name} />
                    <div style={{ minWidth: 0 }}>
                      <div className="primary">
                        {s.name}
                        {s.id === admin?.id && <span className="faint"> (you)</span>}
                      </div>
                      <div className="secondary truncate">{s.email}</div>
                    </div>
                  </div>
                ),
              },
              { key: 'role', header: 'Role', cell: role },
              { key: 'last', header: 'Last signed in', className: 'nowrap wide-only', cell: (s) => (s.lastLoginAt ? dateTime(s.lastLoginAt) : <span className="faint">Never</span>) },
              { key: 'state', header: 'Status', cell: state },
              ...(isOwner ? [{ key: 'act', header: '', className: 'right', cell: controls }] : []),
            ]}
            card={(s) => (
              <RowCard
                lead={<Avatar name={s.name} />}
                title={`${s.name}${s.id === admin?.id ? ' (you)' : ''}`}
                sub={`${s.email} · ${s.lastLoginAt ? `signed in ${dateTime(s.lastLoginAt)}` : 'never signed in'}`}
                side={
                  <>
                    {role(s)}
                    {!s.isActive && state(s)}
                  </>
                }
              />
            )}
            phoneExtra={controls}
          />
        )}
      </section>
      {adding && <AddStaff onClose={() => setAdding(false)} onDone={(t) => show(t)} />}
    </div>
  );
}
